import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  message,
  Popover,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import * as XLSX from 'xlsx';
import axios from 'util/Api';
import { faviconDynamicFunction, getErrorMessage } from '../../../util/methods';

const { Text } = Typography;

const normalizeHeader = (value) => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
};

const toText = (value) => {
  if (value == null) return '';
  return String(value).trim();
};

const parseCsvFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = event && event.target ? event.target.result : null;
        const workbook = XLSX.read(data, { type: 'array' });
        if (!workbook || !workbook.SheetNames || !workbook.SheetNames.length) {
          return reject(new Error('No sheet found in selected file.'));
        }

        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        resolve(rawRows);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Unable to read file'));
    reader.readAsArrayBuffer(file);
  });
};

const getStatusTag = (status, text) => {
  if (status === 'success') {
    return <Tag color="green">Success</Tag>;
  }

  if (status === 'error') {
    return <Tag color="red">{text || 'Failed'}</Tag>;
  }

  if (status === 'processing') {
    return <Tag color="blue">Updating...</Tag>;
  }

  return <Tag>Pending</Tag>;
};

const BulkAssetUpdate = ({ authUser }) => {
  const [file, setFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  faviconDynamicFunction(authUser);

  const templateContent = (
    <div>
      <p>CSV file template</p>
      <Button
        size="small"
        onClick={() => {
          window.open('../csv_template/BulkAssetUpdateTemplate.csv', '_blank');
        }}
      >
        Download
      </Button>
    </div>
  );

  const resetPreview = () => {
    setPreviewRows([]);
    setSelectedRowKeys([]);
    setFile(null);
  };

  const handleFileChange = (event) => {
    const selectedFile = event && event.target && event.target.files && event.target.files[0];
    setFile(selectedFile || null);
  };

  const onPreview = async (event) => {
    event.preventDefault();

    if (!file) {
      message.warning('Please choose a CSV file.');
      return;
    }

    if (!file.name || !file.name.toLowerCase().endsWith('.csv')) {
      message.warning('Only CSV files are supported.');
      return;
    }

    setPreviewLoading(true);
    try {
      const rawRows = await parseCsvFile(file);
      if (!rawRows || !rawRows.length) {
        message.warning('CSV file is empty.');
        setPreviewRows([]);
        setSelectedRowKeys([]);
        return;
      }

      const rows = rawRows
        .map((row, index) => {
          const normalizedRow = {};
          Object.keys(row || {}).forEach((header) => {
            normalizedRow[normalizeHeader(header)] = row[header];
          });

          const assetCode = toText(normalizedRow.assetcode);
          const altName = toText(normalizedRow.altname);
          const ownerCode = toText(normalizedRow.ownercode);

          if (!assetCode && !altName && !ownerCode) {
            return null;
          }

          const errors = [];
          if (!assetCode) {
            errors.push('Asset Code is required');
          }
          if (!ownerCode) {
            errors.push('Owner Code is required');
          }

          return {
            key: `row-${index + 1}`,
            sl: index + 1,
            assetCode,
            altName,
            ownerCode,
            isValid: !errors.length,
            updateStatus: errors.length ? 'error' : 'idle',
            updateMessage: errors.length ? errors.join(', ') : ''
          };
        })
        .filter(Boolean)
        .map((row, index) => ({
          ...row,
          key: `row-${index + 1}`,
          sl: index + 1
        }));

      if (!rows.length) {
        message.warning('No usable rows found. Required columns: Asset Code, Alt Name, Owner Code.');
        setPreviewRows([]);
        setSelectedRowKeys([]);
        return;
      }

      const defaultSelected = rows.filter((row) => row.isValid).map((row) => row.key);
      setPreviewRows(rows);
      setSelectedRowKeys(defaultSelected);

      const invalidCount = rows.length - defaultSelected.length;
      if (invalidCount > 0) {
        message.warning(`Loaded ${rows.length} row(s). ${invalidCount} invalid row(s) are excluded from selection.`);
      } else {
        message.success(`Loaded ${rows.length} row(s) for update.`);
      }
    } catch (error) {
      message.error(getErrorMessage(error) || 'Failed to parse CSV file.');
      setPreviewRows([]);
      setSelectedRowKeys([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  const allSelectableRowKeys = useMemo(() => {
    return previewRows.filter((row) => row.isValid).map((row) => row.key);
  }, [previewRows]);

  const allChecked = allSelectableRowKeys.length > 0 && selectedRowKeys.length === allSelectableRowKeys.length;
  const indeterminate = selectedRowKeys.length > 0 && selectedRowKeys.length < allSelectableRowKeys.length;

  const onCheckAll = (event) => {
    const checked = event && event.target && event.target.checked;
    setSelectedRowKeys(checked ? allSelectableRowKeys : []);
  };

  const onBulkUpdate = async () => {
    if (!selectedRowKeys.length) {
      message.warning('Select at least one row to update.');
      return;
    }

    const selectedRows = previewRows.filter((row) => selectedRowKeys.includes(row.key));

    const duplicates = new Set();
    const seenCodes = new Set();
    selectedRows.forEach((row) => {
      const code = toText(row.assetCode).toLowerCase();
      if (!code) {
        return;
      }
      if (seenCodes.has(code)) {
        duplicates.add(code);
      } else {
        seenCodes.add(code);
      }
    });

    if (duplicates.size > 0) {
      message.warning('Duplicate Asset Code found in selected rows. Keep only one row per asset code.');
      setPreviewRows((prevRows) => prevRows.map((row) => {
        const code = toText(row.assetCode).toLowerCase();
        if (!selectedRowKeys.includes(row.key)) {
          return row;
        }
        if (!duplicates.has(code)) {
          return row;
        }
        return {
          ...row,
          updateStatus: 'error',
          updateMessage: 'Duplicate Asset Code selected'
        };
      }));
      return;
    }

    setUpdateLoading(true);
    setPreviewRows((prevRows) => prevRows.map((row) => {
      if (!selectedRowKeys.includes(row.key)) {
        return row;
      }
      return {
        ...row,
        updateStatus: 'processing',
        updateMessage: ''
      };
    }));

    try {
      const payload = {
        assets: selectedRows.map((row) => ({
          assetCode: row.assetCode,
          altName: row.altName || null,
          ownerCode: row.ownerCode
        }))
      };

      const response = await axios.put('/asset/bulk-update-owner-altname', payload);
      const responseData = response && response.data ? response.data : {};
      const successCodes = new Set(((responseData.successAssetCodes || []).map((code) => toText(code).toLowerCase())));
      const failedByCode = new Map();

      (responseData.failedRecords || []).forEach((item) => {
        const code = toText(item && item.assetCode).toLowerCase();
        if (!code) {
          return;
        }
        if (!failedByCode.has(code)) {
          failedByCode.set(code, item.message || 'Failed');
        }
      });

      setPreviewRows((prevRows) => prevRows.map((row) => {
        if (!selectedRowKeys.includes(row.key)) {
          return row;
        }

        const code = toText(row.assetCode).toLowerCase();
        if (successCodes.has(code)) {
          return {
            ...row,
            updateStatus: 'success',
            updateMessage: 'Updated'
          };
        }

        return {
          ...row,
          updateStatus: 'error',
          updateMessage: failedByCode.get(code) || 'Failed to update'
        };
      }));

      const failedCount = responseData.failedCount || 0;
      if (failedCount > 0) {
        message.warning(responseData.message || 'Bulk update completed with partial failures.');
      } else {
        message.success(responseData.message || 'Bulk update completed successfully.');
      }
    } catch (error) {
      message.error(getErrorMessage(error) || 'Bulk update failed.');
      setPreviewRows((prevRows) => prevRows.map((row) => {
        if (!selectedRowKeys.includes(row.key)) {
          return row;
        }
        return {
          ...row,
          updateStatus: 'error',
          updateMessage: 'Failed to update'
        };
      }));
    } finally {
      setUpdateLoading(false);
    }
  };

  const columns = [
    {
      title: 'Sl',
      dataIndex: 'sl',
      key: 'sl',
      width: 80
    },
    {
      title: 'Asset Code',
      dataIndex: 'assetCode',
      key: 'assetCode',
      width: 220
    },
    {
      title: 'Alt Name',
      dataIndex: 'altName',
      key: 'altName',
      width: 220,
      render: (value) => value || '-'
    },
    {
      title: 'Owner Code',
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      width: 220
    },
    {
      title: 'Status',
      dataIndex: 'updateStatus',
      key: 'updateStatus',
      width: 280,
      render: (_, row) => (
        <span>
          {getStatusTag(row.updateStatus, row.updateMessage)}
          {row.updateStatus === 'error' && row.updateMessage ? (
            <span style={{ marginLeft: 8, color: '#f5222d' }}>{row.updateMessage}</span>
          ) : null}
        </span>
      )
    }
  ];

  return (
    <Spin spinning={previewLoading || updateLoading}>
      <Card className="gx-card-metrics">
        <h2>Bulk Asset Update</h2>

        <div style={{ marginTop: 24 }}>
          <Form onSubmit={onPreview}>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Text strong style={{ marginRight: 8 }}>CSV File</Text>
              <Popover content={templateContent} title="" trigger="click">
                <Button type="default" icon="download" size="small" style={{ marginRight: 12 }}>Template</Button>
              </Popover>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ width: 360, marginRight: 8 }}
              />
              <Button type="primary" htmlType="submit" loading={previewLoading} style={{ marginTop: 15, marginRight: 8 }}>
                Upload & Preview
              </Button>
              {previewRows.length > 0 ? (
                <Button onClick={resetPreview} style={{ marginTop: 15 }}>Reset</Button>
              ) : null}
            </div>
          </Form>
        </div>

        {previewRows.length > 0 ? (
          <div style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <Checkbox
                  indeterminate={indeterminate}
                  checked={allChecked}
                  onChange={onCheckAll}
                >
                  Check All
                </Checkbox>
                <Text style={{ marginLeft: 12 }}>
                  Total: {previewRows.length} | Selected: {selectedRowKeys.length}
                </Text>
              </div>
              <div style={{ marginTop: 8 }}>
                <Button type="primary" loading={updateLoading} onClick={onBulkUpdate}>Update Selected</Button>
              </div>
            </div>

            <Table
              rowKey="key"
              bordered
              size="middle"
              dataSource={previewRows}
              columns={columns}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                getCheckboxProps: (record) => ({
                  disabled: !record.isValid
                })
              }}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} rows`
              }}
              scroll={{ x: 1100 }}
            />
          </div>
        ) : null}
      </Card>
    </Spin>
  );
};

const mapStateToProps = ({ auth }) => ({
  authUser: auth.authUser
});

export default connect(mapStateToProps)(BulkAssetUpdate);
